import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "../../component/element/table";
import Modal from "../../component/ui/modal";
import { Button } from "../../component/ui/Button";
import SelectOption from "../../component/element/forms/SelectOption";
import TextBox from "../../component/element/forms/TextBox";
import { get_lawyer_options } from "../../services/apis/lawyers";
import { assign_lawyer, get_compliances } from "../../services/apis/compliance";
import { format_date_time } from "../../services/convert/date";
import { useAppDispatch, useAppSelector } from "../../store";
import { setComplianceOffset } from "../../store/slices/compliance";
import { IAssignLawyer, ICompliance } from "../../store/types/compliance";
import { ILawyer } from "../../store/types/lawyers";
import { ISimpleData } from "../../store/types/misc";

const Compliance = () => {
  const dispatch = useAppDispatch();
  const { compliances, loading, loadingComplianceId, limit, offset, total } =
    useAppSelector(({ compliance }) => compliance);
  const [selectedCompliance, setSelectedCompliance] =
    React.useState<ICompliance | null>(null);
  const [lawyers, setLawyers] = React.useState<ILawyer[]>([]);
  const [lawyersLoading, setLawyersLoading] = React.useState(false);
  const [lawyerId, setLawyerId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [lawyerError, setLawyerError] = React.useState("");

  React.useEffect(() => {
    get_compliances(dispatch, offset, limit);
  }, [dispatch, offset, limit]);

  React.useEffect(() => {
    if (!selectedCompliance) return;

    let isCurrent = true;
    setLawyersLoading(true);
    void get_lawyer_options()
      .then((options) => {
        if (isCurrent && options) setLawyers(options);
      })
      .finally(() => {
        if (isCurrent) setLawyersLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [selectedCompliance]);

  const closeAssignModal = () => {
    setSelectedCompliance(null);
    setLawyerId("");
    setNotes("");
    setLawyerError("");
  };

  const handleAssignLawyer = async () => {
    if (!selectedCompliance) return;
    if (!lawyerId) {
      setLawyerError("Select a lawyer to continue.");
      return;
    }

    const assignment: IAssignLawyer = {
      lawyer_id: Number(lawyerId),
      application_number: selectedCompliance.application_number,
      user_id: selectedCompliance.user_id,
      notes,
    };

    const succeeded = await assign_lawyer(
      dispatch,
      assignment,
      selectedCompliance.id,
    );
    if (succeeded) {
      closeAssignModal();
      get_compliances(dispatch, offset, limit);
    }
  };

  const lawyerOptions: ISimpleData[] = lawyers.map((lawyer) => ({
    id: lawyer.id,
    name: `${lawyer.name} (${lawyer.email})`,
  }));

  const columns: ColumnDef<ICompliance>[] = [
    {
      accessorKey: "application_number",
      header: "Application Number",
    },
    {
      accessorKey: "company_name",
      header: "Company",
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-gray-800">
            {row.original.company_name || "N/A"}
          </p>
          {/* <p className="max-w-xs truncate text-xs text-gray-500">
            {row.original.company_address || "N/A"}
          </p> */}
        </div>
      ),
    },
    {
      accessorKey: "business_type",
      header: "Business Type",
    },
    {
      accessorKey: "selected_country",
      header: "Country",
    },
    {
      accessorKey: "selected_services",
      header: "Services",
      cell: ({ row }) => (
        <span>{row.original.selected_services?.join(", ") || "N/A"}</span>
      ),
    },
    {
      accessorKey: "contact_email",
      header: "Contact",
      cell: ({ row }) => (
        <div>
          <p>{row.original.contact_email || "N/A"}</p>
          {/* <p className="text-xs text-gray-500">
            {row.original.contact_phone || "N/A"}
          </p> */}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue<string>();

        const statusStyles: Record<string, string> = {
          pending: "bg-yellow-100/20 text-yellow-800",
          approved: "bg-green-100/10 text-green-800",
        };

        const styles =
          statusStyles[status?.toLowerCase()] ?? "bg-gray-100 text-gray-800";

        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles}`}
          >
            {status || "N/A"}
          </span>
        );
      },
    },
    {
      accessorKey: "submitted_at",
      header: "Submitted",
      cell: ({ getValue }) => {
        const submittedAt = new Date(getValue<Date>());
        return (
          <span>
            {Number.isNaN(submittedAt.getTime())
              ? "N/A"
              : format_date_time(submittedAt)}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          onClick={() => {
            setLawyerId("");
            setNotes("");
            setLawyerError("");
            setSelectedCompliance(row.original);
          }}
        >
          Assign Lawyers
        </Button>
      ),
    },
  ];

  const pageSize = limit > 0 ? limit : 20;
  const currentPage = Math.floor(offset / pageSize) + 1;
  const lastPage = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <div className="w-full">
      <h2 className="mb-6 text-xl font-bold">Compliance</h2>
      {loading && compliances.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500">
          Loading compliance requests...
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={compliances}
          current_page={currentPage}
          last_page={lastPage}
          onNext={(page) =>
            dispatch(setComplianceOffset((page - 1) * pageSize))
          }
          onPrev={(page) =>
            dispatch(setComplianceOffset((page - 1) * pageSize))
          }
        />
      )}
      <Modal
        title="Assign Lawyer"
        open={selectedCompliance !== null}
        onClose={closeAssignModal}
        onOkClick={handleAssignLawyer}
        isLoading={
          selectedCompliance !== null &&
          loadingComplianceId === selectedCompliance.id
        }
        variant="medium"
      >
        <div className="grid gap-4">
          <p className="text-sm text-gray-600">
            Assign a lawyer to application{" "}
            <span className="font-medium text-gray-900">
              {selectedCompliance?.application_number}
            </span>
          </p>
          <div>
            <SelectOption
              items={lawyerOptions}
              label="Lawyer"
              placeholder="Select lawyer"
              value={lawyerId}
              onChange={(value) => {
                setLawyerId(value);
                setLawyerError("");
              }}
            />
            {lawyersLoading && (
              <p className="mt-1 text-sm text-gray-500" role="status">
                Loading lawyers...
              </p>
            )}
            {lawyerError && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {lawyerError}
              </p>
            )}
          </div>
          <TextBox
            name="notes"
            label="Notes (optional)"
            value={notes}
            placeholder="Add assignment notes"
            onChange={(_, value) => setNotes(value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Compliance;
